<script>
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

/**
 * Seção do painel de detalhes da conversa.
 *
 * Diferente do `AccordionItem`, que ela substituiu na #1243, esta seção NÃO
 * colapsa: o conteúdo está sempre à vista. O cliente não clica para descobrir o
 * que existe atrás de um bloco fechado — ele abre chamado no suporte.
 *
 * O punho de arraste fica recolhido (largura zero) e só aparece no hover do
 * cabeçalho, para que a divisória e o título não fiquem ruidosos com seis
 * seções empilhadas. A classe `drag-handle` é contrato com o `vuedraggable`
 * do `ContactPanel.vue`, que a usa como `handle`.
 *
 * A divisória entre seções NÃO mora aqui, e sim no wrapper que o
 * `ContactPanel.vue` põe em volta de cada seção. Tentou-se
 * `border-b ... last:border-b-0` neste `<section>` e não funciona: ele é filho
 * único do wrapper, então `:last-child` casa SEMPRE e a borda nunca aparece.
 * Quem tem irmãos — e portanto um último de verdade — é o wrapper.
 */
export default {
  components: {
    DashboardIcon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    meta: {
      type: String,
      default: '',
    },
  },
};
</script>

<template>
  <section class="px-4 py-4">
    <!-- 8px entre o titulo e o conteudo. O valor mora AQUI, e so aqui: o
         conteudo de cada secao comeca sem padding em cima, senao os dois se
         somam e a folga muda de secao para secao (#1243). -->
    <header class="group flex items-center justify-between gap-2 mb-2">
      <!-- Sem `gap` aqui, e a folga no punho e no contador: o punho tem largura
           zero em repouso, e um `gap` empurraria o titulo para a direita o ano
           inteiro por causa de um irmao invisivel -- desalinhado do conteudo da
           propria secao, que comeca na borda. -->
      <div class="flex items-center min-w-0">
        <span
          v-if="draggable"
          class="drag-handle flex items-center justify-center w-0 overflow-hidden opacity-0 cursor-grab text-subtle transition-all duration-200 group-hover:w-4 group-hover:me-1.5 group-hover:opacity-100 group-focus-within:w-4 group-focus-within:me-1.5 group-focus-within:opacity-100"
        >
          <DashboardIcon
            icon="re-order-dots-outline"
            size="14"
            view-box="0 0 16 16"
          />
        </span>
        <!-- Escala de texto do painel, lida da referencia da #1243
             (Sidebar Detalhes.dc.html). Sao tres papeis, e so tres:

               titulo da secao  12px / 700 / 0.1em / caixa alta / cor do nome
               valor            12.5px / normal     / cor do nome
               rotulo e apoio   12.5px / normal     / cinza legivel

             O titulo NAO se destaca por tamanho: ele mede o mesmo que o valor
             ao lado. Quem o separa e a caixa alta, o entreletras e o peso; a
             cor e a mesma do valor, de propósito. `text-xs` = 12px,
             `tracking-widest` = 0.1em.

             Era `text-xxs` (10px), o tamanho da referencia. Em tela, num
             painel que ja e todo de texto pequeno, ele ficava abaixo do limite
             confortavel de leitura -- e 10px e menor do que qualquer outro
             texto do produto. O `truncate` segura o titulo mais longo, que com
             0.1em de entreletras cresce junto.

             O cinza e `text-muted` (slate-700 no claro), e nao `text-subtle`:
             a referencia escureceu o secundario justamente para ele ficar
             legivel ao lado do valor. -->
        <h4
          class="m-0 text-xs font-bold tracking-widest uppercase truncate text-body"
        >
          {{ title }}
        </h4>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <!-- A contagem fica na PONTA DIREITA, e nao colada no titulo: encostada
             nele ela se lia como parte do nome da secao ("Participantes 1
             pessoa"). -->
        <span v-if="meta" class="text-xs text-muted">{{ meta }}</span>
        <slot name="actions" />
      </div>
    </header>
    <slot />
  </section>
</template>

<style lang="scss" scoped>
@media (prefers-reduced-motion: reduce) {
  .drag-handle {
    transition: none;
  }
}

/* Em tela sem hover o punho precisa nascer visível.
 *
 * O `handle` do Sortable é um teste de alvo do toque: se o `.drag-handle` tem
 * largura zero, nenhum toque cai dentro dele e a seção simplesmente não
 * reordena no celular ou no tablet. O `AccordionItem` que esta seção
 * substituiu não tinha o problema porque o punho era o cabeçalho inteiro.
 *
 * `group-focus-within` também não resolve: nada dentro do cabeçalho recebe
 * foco — o punho não é focável e o título é um `<h4>`. */
@media (hover: none) {
  .drag-handle {
    width: 1rem;
    /* Logica, e nao `margin-right`: em RTL o punho e o item mais a direita da
       linha, entao a margem fisica cairia na borda EXTERNA da secao e o titulo
       encostaria nele. O projeto nao tem flip automatico no build. */
    margin-inline-end: 0.375rem;
    opacity: 1;
  }
}
</style>
